import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import './MobileBreakpoints.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

function QAWidget({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // State für expanded Q&A items
    const [expandedItems, setExpandedItems] = useState(
        data.qaItems?.reduce((acc, item) => ({ ...acc, [item.id]: item.isExpanded || false }), {}) || {}
    );
    
    // ✅ INLINE-EDITING SYSTEM - NEW LAYOUT SYSTEM 2025
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [originalLayout, setOriginalLayout] = useState({});
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // ✅ RESPONSIVE LAYOUT DETECTION - NEW LAYOUT SYSTEM 2025
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    const widgetRef = useRef(null); // Für Delete Modal und Container-Ref

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // Mobile Breakpoint Detection (750px)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Helper functions (consistent with other widgets)
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // Toggle Q&A Item - FIXED: Funktioniert jetzt auch im Preview
    const toggleQAItem = (itemId, event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        
        // ✅ TOGGLE funktioniert IMMER (Edit + Preview Mode)
            setExpandedItems(prev => ({
                ...prev,
                [itemId]: !prev[itemId]
            }));
        
        console.log('🔄 QA Toggle:', itemId, 'New state:', !expandedItems[itemId]);
    };

    // ✅ LAYOUT CONFIGURATION - NEW LAYOUT SYSTEM 2025 mit LayoutEngine Integration
    const getLayoutConfig = () => {
        const layoutType = data.qaLayout || 'classic-side-image';
        
        const layouts = {
            'classic-side-image': {
                direction: 'row',
                imagePosition: 'left',
                qaAlignment: 'left',
                titleAlignment: 'left',
                imageWidth: data.leftImageWidth || 35,
                spacing: 'normal',
                cardStyle: 'clean',
                showImage: data.showLeftImage !== false,
                description: 'Image left, Q&A right - classic layout'
            },
            'full-width-minimal': {
                direction: 'column',
                imagePosition: 'hidden',
                qaAlignment: 'left', // ✅ Links ausgerichtet für bessere Lesbarkeit
                titleAlignment: 'center', // ✅ Titel zentriert
                imageWidth: 0,
                spacing: 'minimal', // ✅ Minimaler Spacing
                cardStyle: 'clean', // ✅ Clean Style für einfaches Design
                showImage: false,
                fullWidth: true, // ✅ Volle Breite aktiviert
                description: 'Full-width Q&A without image - minimal design'
            },
            'magazine-style': {
                direction: 'row',
                imagePosition: 'right',
                qaAlignment: 'left',
                titleAlignment: 'left',
                imageWidth: 40,
                spacing: 'wide',
                cardStyle: 'elegant',
                description: 'Magazine layout - Q&A left, image right'
            },
            'accordion-cards': {
                direction: 'column',
                imagePosition: 'background',
                qaAlignment: 'center',
                titleAlignment: 'center',
                imageWidth: 100,
                spacing: 'comfortable',
                cardStyle: 'premium',
                description: 'Card-based accordion with background image'
            },
            'compact-list': {
                direction: 'column',
                imagePosition: 'hidden',
                qaAlignment: 'left',
                titleAlignment: 'left',
                imageWidth: 0,
                spacing: 'tight',
                cardStyle: 'list',
                description: 'Compact list style - maximum content density'
            }
        };
        
        return layouts[layoutType] || layouts['classic-side-image'];
    };

    const layoutConfig = getLayoutConfig();

    // ✅ INLINE-EDITING FUNCTIONS - NEW LAYOUT SYSTEM 2025
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = (fieldName) => {
        if (!onUpdate || !fieldName || fieldName !== isInlineEditing) return;
        
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    const handleInlineKeyPress = (e, fieldName) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleInlineSubmit(fieldName);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleInlineCancel();
        }
    };

    // Smart Outside-Click Handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isInlineEditing) return;
            
            const isEditingInput = event.target.tagName === 'TEXTAREA' || event.target.tagName === 'INPUT';
            const isActionButton = event.target.hasAttribute('data-action-button');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit');
            const isAddWidgets = event.target.closest('.add-widgets');
            const isPagePreview = event.target.closest('.page_preview');
            
            const isRealOutsideClick = !isEditingInput && 
                                     !isActionButton && 
                                     !isDeleteButton && 
                                     !isWidgetEdit &&
                                     (isPagePreview || !widgetRef.current?.contains(event.target));
            
            if (isRealOutsideClick) {
                if (onUpdate && inlineValue !== undefined) {
                    const updatedData = { ...data, [isInlineEditing]: inlineValue };
                    onUpdate(updatedData);
                }
                setIsInlineEditing(null);
                setInlineValue('');
            }
        };

        if (isInlineEditing) {
            document.addEventListener('mousedown', handleClickOutside, true);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside, true);
            };
        }
    }, [isInlineEditing, inlineValue, data, onUpdate]);

    // ✅ RENDER EDITABLE TEXT FUNCTION - NEW LAYOUT SYSTEM 2025 Standards
    const renderEditableText = (fieldName, currentValue, Element = 'p', style = {}, placeholder = 'Click to edit...', cssClass = '') => {
        // Bestimme die responsive CSS-Klasse basierend auf dem Feld
        const getResponsiveClass = () => {
            if (fieldName.includes('title')) return 'responsive-title-text';
            if (fieldName.includes('question')) return 'responsive-subtitle-text';
            if (fieldName.includes('answer')) return 'responsive-additional-text';
            return '';
        };
        
        const responsiveClass = cssClass || getResponsiveClass();
        
        if (!isEditMode) {
            return currentValue ? (
                <Element 
                    style={{
                        ...style,
                        whiteSpace: style.whiteSpace || 'pre-wrap'
                    }}
                    className={responsiveClass}
                >
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        onBlur={() => handleInlineSubmit(fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        ref={(input) => {
                            if (input) {
                                setTimeout(() => {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }, 0);
                            }
                        }}
                        style={{
                            ...style,
                            border: 'none',
                            background: 'transparent',
                            outline: 'none',
                            width: '100%',
                            color: '#333333',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            textAlign: style.textAlign || 'inherit'
                        }}
                    />
                    {/* Action Buttons */}
                    <div style={{
                        position: 'absolute',
                        top: '-11px',
                        right: '-6px',
                        display: 'flex',
                        gap: '4px',
                        zIndex: 9999,
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '2px 4px',
                        borderRadius: '6px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}>
                        <button
                            data-action-button="save"
                            onClick={() => handleInlineSubmit(fieldName)}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                fontSize: '9px',
                                cursor: 'pointer'
                            }}
                        >
                            ✓
                        </button>
                        <button
                            data-action-button="cancel"
                            onClick={handleInlineCancel}
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                fontSize: '9px',
                                cursor: 'pointer'
                            }}
                        >
                            ×
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <Element 
                style={{ 
                    ...style, 
                    cursor: 'text',
                    position: 'relative',
                    padding: '4px 8px',
                    borderRadius: '4px'
                }}
                className={responsiveClass}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleInlineEdit(fieldName, currentValue);
                }}
                title="Click to edit"
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // ✅ RESPONSIVE WIDGET STYLES - NEW LAYOUT SYSTEM 2025
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%'
    });

    const widgetStyle = {
        ...layoutStyles,
        // ✅ FALLBACK für alte Height-Property (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1200) : // Mobile Default
                (data.height || 600)         // Desktop Default
            )
        })
    };

    const titleStyle = {
        color: data.titleColor,
        // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
        // fontSize: appendPx(isMobileLayout ? 
        //     (data.titleSizeMobile || data.titleSize || 36) : 
        //     (data.titleSize || 36)
        // ),
        // fontWeight: isMobileLayout ? 
        //     (data.titleWeightMobile || data.titleWeight || '600') : 
        //     (data.titleWeight || '600'),
        fontFamily: data.titleFont || fonts.globalBigFont,
        lineHeight: data.titleLineHeight,
        // CSS Custom Properties für responsive Fonts
        '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 36),
        '--normal-size': appendPx(data.titleSize || 36),
        '--big-size': appendPx(data.titleSizeBig || data.titleSize || 36),
        '--mobile-weight': data.titleWeightMobile || data.titleWeight || '600',
        '--normal-weight': data.titleWeight || '600',
        '--big-weight': data.titleWeightBig || data.titleWeight || '600',
        marginBottom: appendPx(data.titleMarginBottom),
        margin: 0,
        textAlign: data.titleAlignment || 'center', // ✅ Dynamische Title-Alignment
        // ✅ Standard Padding für Titel
        padding: layoutConfig.fullWidth ? 
            `4px ${isMobileLayout ? 
                (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 24)) :
                (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(24))
            }` : 
            '4px 12px', // ✅ Standard Padding: 4px top/bottom, 12px left/right
        // ✅ Responsive Title Margins - Desktop/Mobile getrennt
        marginLeft: isMobileLayout ? 
            (data.titleMarginLeftMobile !== undefined ? appendPx(data.titleMarginLeftMobile) : appendPx(data.titleMarginLeft || 0)) :
            (data.titleMarginLeft !== undefined ? appendPx(data.titleMarginLeft) : appendPx(0)),
        marginRight: isMobileLayout ? 
            (data.titleMarginRightMobile !== undefined ? appendPx(data.titleMarginRightMobile) : appendPx(data.titleMarginRight || 0)) :
            (data.titleMarginRight !== undefined ? appendPx(data.titleMarginRight) : appendPx(0)),
        width: layoutConfig.fullWidth ? '100%' : 'auto', // ✅ Volle Breite für full-width Layout
        boxSizing: 'border-box', // ✅ Padding wird in width eingerechnet
        ...getTextEffectStyles(data.titleEffect)
    };

    // LAYOUT-BASED STYLING SYSTEM
    const getLayoutStyles = () => {
        const spacing = {
            minimal: { gap: '0px', padding: '0px' }, // ✅ Minimal für full-width
            tight: { gap: '16px', padding: '16px' },
            normal: { gap: '24px', padding: '24px' },
            comfortable: { gap: '32px', padding: '32px' },
            wide: { gap: '48px', padding: '48px' }
        };
        
        const currentSpacing = spacing[layoutConfig.spacing] || spacing.normal;
        
        return {
            container: {
                display: 'flex',
                flexDirection: isMobileLayout ? 'column' : layoutConfig.direction,
                gap: isMobileLayout ? '20px' : currentSpacing.gap,
                alignItems: 'center', // ✅ Horizontal centering
                justifyContent: 'center', // ✅ Vertical centering
                minHeight: '100%',
                padding: layoutConfig.fullWidth ? '0px' : currentSpacing.padding, // ✅ Kein Padding für full-width
                width: layoutConfig.fullWidth ? '100%' : 'auto' // ✅ Volle Breite für full-width
            },
            image: {
                width: layoutConfig.imagePosition === 'hidden' ? '0' : 
                       (isMobileLayout ? '100%' : `${layoutConfig.imageWidth}%`),
                height: isMobileLayout ? 
                    'auto' : // ✅ Mobile Height auf auto für natürliche Proportionen
                    appendPx(data.leftImageHeight || 300),
                maxHeight: isMobileLayout && data.heroImageMaxHeightMobile ? 
                    appendPx(data.heroImageMaxHeightMobile) : 'none',
                borderRadius: data.leftImageBorderRadius !== undefined ? appendPx(data.leftImageBorderRadius) : appendPx(12),
                objectFit: data.leftImageFit || 'cover',
                flexShrink: 0,
                order: layoutConfig.imagePosition === 'right' && !isMobileLayout ? 2 : 1,
                display: layoutConfig.imagePosition === 'hidden' ? 'none' : 'block'
            },
            qaContainer: {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: layoutConfig.qaAlignment === 'center' ? 'center' : 'stretch',
                justifyContent: 'center', // ✅ Vertical centering für Q&A Items
                textAlign: layoutConfig.qaAlignment,
                order: layoutConfig.imagePosition === 'right' && !isMobileLayout ? 1 : 2,
                width: (layoutConfig.fullWidth || isMobileLayout) ? '100%' : 'auto', // ✅ Mobile auch 100% Breite
                maxWidth: (layoutConfig.fullWidth || isMobileLayout) ? 'none' : '100%' // ✅ Mobile keine Begrenzung
            }
        };
    };
    
    const qaLayoutStyles = getLayoutStyles();

    // QA ITEM STYLING basierend auf Layout
    const getQAItemStyle = (isExpanded, index) => {
        // ✅ REVOLUTIONÄRER BORDER STYLE SYSTEM mit Last Item Support
        const getBorderStyle = () => {
            const borderStyle = data.qaItemBorderStyle || 'border';
            const borderWidth = data.qaItemBorderWidth !== undefined ? data.qaItemBorderWidth : 1;
            const borderColor = data.qaItemBorderColor || '#e0e0e0';
            const isLastItem = index === (data.qaItems?.length - 1);
            
            // ✅ 0px Border Width = No Border (wie 'none' behandeln)
            if (borderWidth === 0) {
                return { border: 'none', borderBottom: 'none' };
            }
            
            switch (borderStyle) {
                case 'none':
                    return { border: 'none', borderBottom: 'none' };
                case 'underline':
                    return { 
                        border: 'none', 
                        borderBottom: `${borderWidth}px solid ${borderColor}` 
                    };
                case 'frameless':
                    return { 
                        // ✅ Frameless: Intelligentes Border System - verhindert Überlappungen
                        border: 'none', // Reset all borders first
                        borderTop: `${borderWidth}px solid ${borderColor}`,
                        borderLeft: `${borderWidth}px solid ${borderColor}`,
                        borderRight: `${borderWidth}px solid ${borderColor}`,
                        // ✅ Nur letztes Item bekommt Border Bottom für nahtloses Design
                        borderBottom: isLastItem ? `${borderWidth}px solid ${borderColor}` : 'none'
                    };
                case 'border':
                default:
                    return { 
                        // ✅ Full Border: Echter Full Border um jedes einzelne Item
                        border: `${borderWidth}px solid ${borderColor}`
                    };
            }
        };
        
        const borderStyles = getBorderStyle();
        
        const baseStyle = {
            backgroundColor: data.qaItemBackgroundColor || 'transparent',
            ...borderStyles,
            borderRadius: data.qaItemBorderRadius !== undefined ? appendPx(data.qaItemBorderRadius) : appendPx(8),
            marginBottom: data.qaItemSpacing !== undefined ? appendPx(data.qaItemSpacing) : appendPx(10), // ✅ Fix für 0 Wert Bug - undefined check
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            overflow: 'hidden',
            // ✅ Mobile: Q&A Items auf 100% Breite setzen
            width: isMobileLayout ? '100%' : 'auto',
            maxWidth: isMobileLayout ? 'none' : '100%'
        };
        
        // LAYOUT-SPEZIFISCHE STYLES
        switch (layoutConfig.cardStyle) {
            case 'minimal':
                return {
                    ...baseStyle,
                    backgroundColor: 'transparent',
                    // ✅ Respektiert das neue Border Style System
                    ...(data.qaItemBorderStyle === 'none' ? 
                        { border: 'none', borderBottom: 'none' } : 
                        { border: 'none', borderBottom: `${data.qaItemBorderWidth !== undefined ? data.qaItemBorderWidth : 1}px solid ${data.qaItemBorderColor || '#e0e0e0'}` }
                    ),
                    borderRadius: '0',
                    padding: '0',
                    width: '100%', // ✅ Volle Breite
                    maxWidth: 'none' // ✅ Keine Begrenzung
                };
            
            case 'modern':
                return {
                    ...baseStyle,
                    backgroundColor: data.qaItemBackgroundColor || '#ffffff',
                    // ✅ Modern Style überschreibt Border für Shadow-Effekt (außer bei 'underline')
                    ...(data.qaItemBorderStyle === 'underline' ? 
                        {} : // Behält baseStyle border
                        { border: 'none', borderBottom: 'none' }
                    ),
                    borderRadius: '16px',
                    boxShadow: isExpanded ? 
                        '0 8px 25px rgba(0, 0, 0, 0.1)' : 
                        '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transform: isExpanded ? 'translateY(-2px)' : 'translateY(0)'
                };
            
            case 'elegant':
                return {
                    ...baseStyle,
                    backgroundColor: data.qaItemBackgroundColor || '#fafafa',
                    // ✅ Elegant Style respektiert Border System vollständig
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                };
            
            case 'premium':
                return {
                    ...baseStyle,
                    background: `linear-gradient(135deg, ${data.qaItemBackgroundColor || '#ffffff'} 0%, ${data.qaItemHoverBackground || '#f8f9fa'} 100%)`,
                    // ✅ Premium Style respektiert Border System mit enhanced styling
                    borderRadius: '20px',
                    boxShadow: isExpanded ? 
                        '0 12px 35px rgba(102, 126, 234, 0.15)' : 
                        '0 4px 15px rgba(102, 126, 234, 0.08)',
                    backdropFilter: 'blur(10px)'
                };
            
            case 'list':
                return {
                    ...baseStyle,
                    backgroundColor: 'transparent',
                    // ✅ List Style hat eigene Border Logic (Left Border)
                    border: 'none',
                    borderBottom: 'none',
                    borderLeft: data.qaItemBorderStyle !== 'none' ? 
                        `${((data.qaItemBorderWidth !== undefined ? data.qaItemBorderWidth : 1) * 2)}px solid ${data.qaItemBorderColor || '#667eea'}` : 
                        'none',
                    borderRadius: '0',
                    paddingLeft: data.qaItemBorderStyle !== 'none' ? '16px' : '0'
                };
            
            default: // 'clean'
                return {
                    ...baseStyle,
                    // ✅ Full-width für clean style wenn fullWidth Layout aktiv
                    ...(layoutConfig.fullWidth && {
                        width: '100%',
                        maxWidth: 'none',
                        margin: '0',
                        padding: '0',
                        // ✅ Respektiert das neue Border Style System auch bei full-width
                        ...(data.qaItemBorderStyle === 'none' ? 
                            { border: 'none', borderBottom: 'none' } : 
                            data.qaItemBorderStyle === 'underline' ? 
                                { border: 'none', borderBottom: `${data.qaItemBorderWidth !== undefined ? data.qaItemBorderWidth : 1}px solid ${data.qaItemBorderColor || '#e0e0e0'}` } :
                                {} // Behält baseStyle border für 'border' option
                        ),
                        borderRadius: '0'
                    })
                };
        }
    };

    const questionStyle = {
        color: data.questionColor,
        // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
        // fontSize: appendPx(isMobileLayout ? 
        //     (data.questionSizeMobile || data.questionSize || 16) : 
        //     (data.questionSize || 16)
        // ),
        // fontWeight: isMobileLayout ? 
        //     (data.questionWeightMobile || data.questionWeight || '600') : 
        //     (data.questionWeight || '600'),
        fontFamily: data.questionFont || fonts.globalSmallFont, // ✅ Secondary Font (gleich wie Answers)
        lineHeight: data.questionLineHeight,
        // CSS Custom Properties für responsive Fonts
        '--mobile-size': appendPx(data.questionSizeMobile || data.questionSize || 16),
        '--normal-size': appendPx(data.questionSize || 16),
        '--big-size': appendPx(data.questionSizeBig || data.questionSize || 16),
        '--mobile-weight': data.questionWeightMobile || data.questionWeight || '600',
        '--normal-weight': data.questionWeight || '600',
        '--big-weight': data.questionWeightBig || data.questionWeight || '600',
        margin: 0,
        // ✅ Separate Vertical/Horizontal Padding für Questions
        padding: layoutConfig.fullWidth ? 
            `${isMobileLayout ? 
                (data.qaItemPaddingVerticalMobile !== undefined ? appendPx(data.qaItemPaddingVerticalMobile) : appendPx(data.qaItemPaddingVertical || 20)) :
                (data.qaItemPaddingVertical !== undefined ? appendPx(data.qaItemPaddingVertical) : appendPx(20))
            } 0` : 
            `${isMobileLayout ? 
                (data.qaItemPaddingVerticalMobile !== undefined ? appendPx(data.qaItemPaddingVerticalMobile) : appendPx(data.qaItemPaddingVertical || 20)) :
                (data.qaItemPaddingVertical !== undefined ? appendPx(data.qaItemPaddingVertical) : appendPx(20))
            } ${isMobileLayout ? 
                (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 20)) :
                (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(20))
            }`,
        display: 'flex', // ✅ Immer flex
        flexDirection: 'row', // ✅ Immer row - Frage links, Plus rechts
        justifyContent: 'space-between', // ✅ Frage links, Icon rechts
        alignItems: 'center',
        textAlign: 'left', // ✅ Frage immer linksbündig
        width: layoutConfig.fullWidth ? '100%' : 'auto', // ✅ Volle Breite für Questions
        ...getTextEffectStyles(data.questionEffect)
    };

    const answerStyle = {
        color: data.answerColor,
        // fontSize und fontWeight werden über CSS Custom Properties + responsive CSS-Klassen gesteuert
        // fontSize: appendPx(isMobileLayout ? 
        //     (data.answerSizeMobile || data.answerSize || 15) : 
        //     (data.answerSize || 15)
        // ),
        // fontWeight: isMobileLayout ? 
        //     (data.answerWeightMobile || data.answerWeight || '400') : 
        //     (data.answerWeight || '400'),
        fontFamily: data.answerFont || fonts.globalSmallFont,
        lineHeight: data.answerLineHeight,
        // CSS Custom Properties für responsive Fonts
        '--mobile-size': appendPx(data.answerSizeMobile || data.answerSize || 15),
        '--normal-size': appendPx(data.answerSize || 15),
        '--big-size': appendPx(data.answerSizeBig || data.answerSize || 15),
        '--mobile-weight': data.answerWeightMobile || data.answerWeight || '400',
        '--normal-weight': data.answerWeight || '400',
        '--big-weight': data.answerWeightBig || data.answerWeight || '400',
        margin: 0,
        // ✅ Separate Vertical/Horizontal Padding für Answers
        padding: layoutConfig.fullWidth ? 
            `0 0 ${isMobileLayout ? 
                (data.qaItemPaddingVerticalMobile !== undefined ? appendPx(data.qaItemPaddingVerticalMobile) : appendPx(data.qaItemPaddingVertical || 20)) :
                (data.qaItemPaddingVertical !== undefined ? appendPx(data.qaItemPaddingVertical) : appendPx(20))
            } 0` : 
            `0 ${isMobileLayout ? 
                (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 20)) :
                (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(20))
            } ${isMobileLayout ? 
                (data.qaItemPaddingVerticalMobile !== undefined ? appendPx(data.qaItemPaddingVerticalMobile) : appendPx(data.qaItemPaddingVertical || 20)) :
                (data.qaItemPaddingVertical !== undefined ? appendPx(data.qaItemPaddingVertical) : appendPx(20))
            } ${isMobileLayout ? 
                (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 20)) :
                (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(20))
            }`,
        textAlign: layoutConfig.qaAlignment || 'left',
        width: layoutConfig.fullWidth ? '100%' : 'auto', // ✅ Volle Breite für Answers
        ...getTextEffectStyles(data.answerEffect)
    };

    const iconStyle = {
        color: data.iconColor,
        fontSize: appendPx(data.iconSize),
        fontWeight: data.iconWeight,
        userSelect: 'none',
        flexShrink: 0
    };

    // Handle widget click for selection
    const handleWidgetClick = (e) => {
        if (isEditMode && onSelectWidget && !isSelected) {
            e.stopPropagation();
            onSelectWidget(data.id);
        }
    };

    // ENHANCED MOBILE-OPTIMIZED RESPONSIVE STYLES
    const responsiveStyles = `
        /* === MOBILE OPTIMIZATION (750px and below) === */
        @media (max-width: 750px) {
            .qa-widget-content {
                flex-direction: column !important;
                gap: 16px !important;
                padding: 16px !important;
            }
            
            /* Mobile Image Handling */
            .qa-widget-image {
                width: 100% !important;
                height: ${data.mobileImageHeight || 200}px !important;
                margin-bottom: 16px !important;
                order: 1 !important;
                ${data.mobileImagePosition === 'hidden' ? 'display: none !important;' : ''}
            }
            
            /* Mobile Typography - DEAKTIVIERT: Jetzt über isMobileLayout gesteuert */
            /* 
            .qa-widget h2 {
                font-size: ${data.titleSizeMobile || data.titleSize || 28}px !important;
                font-weight: ${data.titleWeightMobile || data.titleWeight || '600'} !important;
                text-align: center !important;
                margin-bottom: 20px !important;
                line-height: 1.2 !important;
            }
            */
            
            /* Mobile Q&A Items - Layout nur, keine Fonts */
            .qa-widget-item {
                margin-bottom: 12px !important;
                border-radius: 12px !important;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
            }
            
            /* Mobile Questions - DEAKTIVIERT: Jetzt über isMobileLayout gesteuert */
            /* 
            .qa-widget-question {
                font-size: ${data.questionSizeMobile || data.questionSize || 15}px !important;
                font-weight: ${data.questionWeightMobile || data.questionWeight || '600'} !important;
                padding: 16px !important;
                line-height: 1.4 !important;
                flex-direction: row !important;
                justify-content: space-between !important;
                align-items: center !important;
            }
            */
            
            /* Mobile Answers - DEAKTIVIERT: Jetzt über isMobileLayout gesteuert */
            /* 
            .qa-widget-answer {
                font-size: ${data.answerSizeMobile || data.answerSize || 14}px !important;
                font-weight: ${data.answerWeightMobile || data.answerWeight || '400'} !important;
                padding: 0 16px 16px 16px !important;
                line-height: 1.6 !important;
                text-align: left !important;
            }
            */
            
            /* Mobile Layout Specific Adjustments */
            .qa-widget.layout-modern .qa-widget-item {
                border-radius: 16px !important;
                margin-bottom: 16px !important;
            }
            
            .qa-widget.layout-premium .qa-widget-item {
                border-radius: 20px !important;
                padding: 0 !important;
            }
            
            .qa-widget.layout-minimal .qa-widget-item {
                border-radius: 0 !important;
                border-left: none !important;
                border-bottom: 1px solid #e5e7eb !important;
                padding: 16px 0 !important;
            }
            
            .qa-widget.layout-list .qa-widget-item {
                border-left: 3px solid #667eea !important;
                padding-left: 12px !important;
                margin-bottom: 16px !important;
            }
            
            /* Mobile Touch Targets */
            .qa-widget-item {
                min-height: 48px !important;
            }
            
            .qa-widget-question {
                min-height: 48px !important;
                cursor: pointer !important;
                -webkit-tap-highlight-color: rgba(102, 126, 234, 0.1) !important;
            }
            
            /* Mobile Centered Layout Fix */
            .qa-widget.layout-modern .qa-widget-question,
            .qa-widget .layout-image-top-centered .qa-widget-question {
                text-align: center !important;
                justify-content: center !important;
                flex-direction: column !important;
                gap: 8px !important;
            }
            
            .qa-widget.layout-modern .qa-widget-answer,
            .qa-widget .layout-image-top-centered .qa-widget-answer {
                text-align: center !important;
            }
        }
        
        /* === TABLET OPTIMIZATION (751px - 1023px) === */
        @media (min-width: 751px) and (max-width: 1023px) {
            .qa-widget-content {
                gap: 20px !important;
                padding: 20px !important;
            }
            
            .qa-widget-image {
                height: ${Math.round((data.leftImageHeight || 300) * 0.8)}px !important;
            }
            
            .qa-widget h2 {
                font-size: ${Math.round((data.titleSize || 32) * 0.9)}px !important;
            }
            
            .qa-widget-question {
                font-size: ${Math.round((data.questionSize || 16) * 0.95)}px !important;
                padding: 18px !important;
            }
            
            .qa-widget-answer {
                font-size: ${Math.round((data.answerSize || 15) * 0.95)}px !important;
                padding: 0 18px 18px 18px !important;
            }
        }
        
        /* === LARGE SCREEN OPTIMIZATION (1400px+) === */
        @media (min-width: 1400px) {
            .qa-widget h2 {
                font-size: ${data.titleSizeBig || data.titleSize || 36}px !important;
            }
            
            .qa-widget-question {
                font-size: ${data.questionSizeBig || data.questionSize || 18}px !important;
            }
            
            .qa-widget-answer {
                font-size: ${data.answerSizeBig || data.answerSize || 16}px !important;
            }
            
            .qa-widget-content {
                padding: 32px !important;
                gap: 32px !important;
            }
        }
        
        /* === MOBILE LANDSCAPE OPTIMIZATION === */
        @media (max-width: 750px) and (orientation: landscape) {
            .qa-widget-image {
                height: ${Math.round((data.mobileImageHeight || 200) * 0.7)}px !important;
            }
            
            .qa-widget-content {
                padding: 12px !important;
            }
            
            .qa-widget h2 {
                font-size: ${Math.round((data.titleSizeMobile || data.titleSize || 28) * 0.9)}px !important;
                margin-bottom: 16px !important;
            }
        }
    `;

    return (
        <>
            {/* CSS Animations & Responsive Styles */}
            <style>
                {`
                    @keyframes fadeInUp {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                    
                    .qa-widget-item {
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
                    }
                    
                    .qa-widget-item.layout-modern:hover {
                        transform: translateY(-4px) !important;
                    }
                    
                    .qa-widget-item.layout-premium {
                        backdrop-filter: blur(10px);
                    }
                    
                    ${responsiveStyles}
                `}
            </style>
            <div 
                style={{
                    ...widgetStyle,
                    position: 'relative',
                    overflow: layoutConfig.imagePosition === 'background' ? 'hidden' : 'visible'
                }}
            onClick={handleWidgetClick}
                className={`qa-widget layout-${layoutConfig.cardStyle} ${isSelected ? 'selected' : ''}${isMobileLayout ? ' mobile-layout' : ''}`}
            ref={(el) => { containerRef.current = el; widgetRef.current = el; }}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'QAWidget'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [QAWIDGET] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}

                {/* Title - Mit Show/Hide Toggle */}
                {data.showTitleText !== false && renderEditableText('titleText', data.titleText, 'h2', titleStyle, 'Enter Q&A title...')}

                {/* Title Content Spacing - Responsive für Desktop/Mobile */}
                {data.showTitleText !== false && data.titleText && (
                    <div style={{
                        height: isMobileLayout ? 
                            (data.titleContentSpacingMobile !== undefined ? appendPx(data.titleContentSpacingMobile) : appendPx(data.titleContentSpacing || 40)) :
                            (data.titleContentSpacing !== undefined ? appendPx(data.titleContentSpacing) : appendPx(40))
                    }} />
                )}

                {/* Main Content Container - LAYOUT-BASED mit vertikaler Zentrierung */}
                <div style={{
                    ...qaLayoutStyles.container,
                    minHeight: 'inherit', // Erbt die Widget-Höhe
                    alignItems: 'center', // ✅ Horizontal centering
                    justifyContent: 'center' // ✅ Vertical centering
                }} className="qa-widget-content">
                    {/* Image - Position basierend auf Layout */}
                    {data.showLeftImage && data.leftImage && layoutConfig.imagePosition !== 'hidden' && (
                        <div style={{
                            ...qaLayoutStyles.image,
                            // ✅ Image Margin Bottom - Responsive für Desktop/Mobile
                            marginBottom: isMobileLayout ? 
                                (data.imageMarginBottomMobile !== undefined ? appendPx(data.imageMarginBottomMobile) : appendPx(data.imageMarginBottom || 20)) :
                                (data.imageMarginBottom !== undefined ? appendPx(data.imageMarginBottom) : appendPx(20)),
                            // Background Image für 'accordion-cards' Layout
                            ...(layoutConfig.imagePosition === 'background' && {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 0,
                                opacity: 0.1,
                                marginBottom: '0' // ✅ Background Images haben keinen Margin
                            })
                        }}>
                            {layoutConfig.imagePosition === 'background' ? (
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundImage: `url(${data.leftImage})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    borderRadius: qaLayoutStyles.image.borderRadius
                                }} />
                            ) : (
                        <img
                            src={data.leftImage}
                            alt="Q&A"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: qaLayoutStyles.image.objectFit,
                                        borderRadius: qaLayoutStyles.image.borderRadius
                                    }}
                                    className="qa-widget-image"
                                />
                            )}
                        </div>
                    )}

                    {/* Q&A Container - LAYOUT-AWARE */}
                    <div style={{
                        ...qaLayoutStyles.qaContainer,
                        position: 'relative',
                        zIndex: layoutConfig.imagePosition === 'background' ? 1 : 'auto'
                    }}>
                        {data.qaItems?.map((item, index) => {
                            const isExpanded = expandedItems[item.id];
                            
                            return (
                                <div
                                    key={item.id}
                                    style={getQAItemStyle(isExpanded, index)}
                                    className={`qa-widget-item layout-${layoutConfig.cardStyle}`}
                                    onMouseEnter={(e) => {
                                        if (data.enableHoverEffects) {
                                            // Layout-spezifische Hover-Effekte
                                            switch (layoutConfig.cardStyle) {
                                                case 'modern':
                                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                                    e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 0, 0, 0.15)';
                                                    break;
                                                case 'premium':
                                                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                                    break;
                                                default:
                                                    e.currentTarget.style.backgroundColor = data.qaItemHoverBackground || '#f8f9fa';
                                            }
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (data.enableHoverEffects) {
                                            // Reset hover effects
                                            switch (layoutConfig.cardStyle) {
                                                case 'modern':
                                                    e.currentTarget.style.transform = isExpanded ? 'translateY(-2px)' : 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = isExpanded ? 
                                                        '0 8px 25px rgba(0, 0, 0, 0.1)' : 
                                                        '0 2px 8px rgba(0, 0, 0, 0.05)';
                                                    break;
                                                case 'premium':
                                                    e.currentTarget.style.borderColor = data.qaItemBorderColor || 'rgba(102, 126, 234, 0.1)';
                                                    break;
                                                default:
                                                    e.currentTarget.style.backgroundColor = data.qaItemBackgroundColor || 'transparent';
                                            }
                                        }
                                    }}
                                    onClick={(e) => {
                                        // ✅ FIXED: Toggle funktioniert jetzt in Edit UND Preview Mode
                                        toggleQAItem(item.id, e);
                                    }}
                                >
                                    {/* Question */}
                                    <div style={{
                                        ...questionStyle,
                                        padding: layoutConfig.cardStyle === 'list' ? 
                                            '12px 0' : 
                                            `${isMobileLayout ? 
                                                (data.qaItemPaddingVerticalMobile !== undefined ? appendPx(data.qaItemPaddingVerticalMobile) : appendPx(data.qaItemPaddingVertical || 20)) :
                                                (data.qaItemPaddingVertical !== undefined ? appendPx(data.qaItemPaddingVertical) : appendPx(20))
                                            } ${isMobileLayout ? 
                                                (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 20)) :
                                                (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(20))
                                            }`
                                    }} className="qa-widget-question responsive-subtitle-text">
                                        <span>{item.question}</span>
                                        <span style={{
                                            ...iconStyle,
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease',
                                            marginTop: '-6px'
                                        }}>
                                            {isExpanded ? (data.collapseIcon || '−') : (data.expandIcon || '+')}
                                        </span>
                                    </div>

                                    {/* Answer - SMOOTH ANIMATION */}
                                    {isExpanded && (
                                        <div 
                                            style={{
                                                ...answerStyle,
                                                padding: layoutConfig.cardStyle === 'list' ? 
                                                    '0 0 12px 0' : 
                                                    `0 ${isMobileLayout ? 
                                                        (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 20)) :
                                                        (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(20))
                                                    } ${isMobileLayout ? 
                                                        (data.qaItemPaddingVerticalMobile !== undefined ? appendPx(data.qaItemPaddingVerticalMobile) : appendPx(data.qaItemPaddingVertical || 20)) :
                                                        (data.qaItemPaddingVertical !== undefined ? appendPx(data.qaItemPaddingVertical) : appendPx(20))
                                                    } ${isMobileLayout ? 
                                                        (data.qaItemPaddingHorizontalMobile !== undefined ? appendPx(data.qaItemPaddingHorizontalMobile) : appendPx(data.qaItemPaddingHorizontal || 20)) :
                                                        (data.qaItemPaddingHorizontal !== undefined ? appendPx(data.qaItemPaddingHorizontal) : appendPx(20))
                                                    }`,
                                                animation: 'fadeInUp 0.3s ease-out'
                                            }} 
                                            className="qa-widget-answer responsive-additional-text"
                                        >
                                            {item.answer}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Delete Modal */}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={() => {
                            if (onRemove) {
                                const mockEvent = { stopPropagation: () => {} };
                                onRemove(mockEvent, data.id);
                            }
                            setShowDeleteModal(false);
                        }}
                    />
                )}
            </div>
        </>
    );
}

export default QAWidget;
